import { Component, input } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ErrorTypeLabels, FieldLabels, DEFAULT_ERROR_TYPE_LABELS } from '../../../shared/interfaces/error-messages.interface';

interface FieldError {
  fieldName: string;
  controlName: string;
  errors: string[];
  formattedErrors?: { [errorType: string]: string };
}

interface ErrorGroup {
  errorType: string;
  errorLabel: string;
  fieldErrors: Array<{ fieldName: string; formattedLabel: string }>;
}

interface DetailedError {
  errorType: string;
  controlName: string;
  formattedLabel: string;
}

@Component({
  selector: 'htx-error-summary-field',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './error-summary-field.component.html',
  styleUrl: './error-summary-field.component.scss'
})
export class ErrorSummaryFieldComponent {
  form = input.required<FormGroup>();
  title = input<string>('Validation Errors');
  controlNames = input<string[] | undefined>(undefined);
  fieldLabels = input<FieldLabels>({});
  errorTypeLabels = input<ErrorTypeLabels>(DEFAULT_ERROR_TYPE_LABELS);

  getFieldErrors(): FieldError[] {
    const errors: FieldError[] = [];
    const controlsToCheck = this.controlNames() || Object.keys(this.form().controls);

    controlsToCheck.forEach((controlName) => {
      const control = this.form().get(controlName);
      if (control && control.invalid && control.touched) {
        const errorKeys = Object.keys(control.errors || {});
        if (errorKeys.length > 0) {
          const formattedErrors: { [errorType: string]: string } = {};

          errorKeys.forEach((errorType) => {
            formattedErrors[errorType] = this.getFormattedErrorTypeLabel(errorType, controlName);
          });

          errors.push({
            fieldName: controlName,
            controlName: controlName,
            errors: errorKeys,
            formattedErrors
          });
        }
      }
    });

    return errors;
  }

  getErrorGroups(): ErrorGroup[] {
    const errors = this.getFieldErrors();
    // Gruppiere nach formattiertem Label, nicht nach errorType
    const groupMap = new Map<string, Array<{ fieldName: string; formattedLabel: string }>>();
    const errorTypeMap = new Map<string, string>(); // Speichere errorType für jedes Label

    errors.forEach((error) => {
      error.errors.forEach((errorType) => {
        const formattedLabel = error.formattedErrors?.[errorType] || this.getErrorTypeLabel(errorType);

        if (!groupMap.has(formattedLabel)) {
          groupMap.set(formattedLabel, []);
          errorTypeMap.set(formattedLabel, errorType);
        }
        groupMap.get(formattedLabel)!.push({
          fieldName: error.controlName,
          formattedLabel
        });
      });
    });

    return Array.from(groupMap.entries()).map(([formattedLabel, fieldErrors]) => ({
      errorType: errorTypeMap.get(formattedLabel) || '',
      errorLabel: formattedLabel,
      fieldErrors
    }));
  }

  getDetailedErrors(): DetailedError[] {
    const errors: DetailedError[] = [];
    const fieldErrors = this.getFieldErrors();

    fieldErrors.forEach((fieldError) => {
      fieldError.errors.forEach((errorType) => {
        errors.push({
          errorType,
          controlName: fieldError.controlName,
          formattedLabel: fieldError.formattedErrors?.[errorType] || errorType
        });
      });
    });

    return errors;
  }

  hasErrors(): boolean {
    return this.getFieldErrors().length > 0;
  }

  getFieldLabel(controlName: string): string {
    const labels = this.fieldLabels();
    return labels[controlName] || controlName;
  }

  getErrorTypeLabel(errorKey: string): string {
    const labels = this.errorTypeLabels();
    return labels[errorKey] || errorKey;
  }

  getFormattedErrorTypeLabel(errorKey: string, controlName: string): string {
    const control = this.form().get(controlName);
    const label = this.getErrorTypeLabel(errorKey);

    if (!control || !control.errors) {
      return label;
    }

    const error = control.errors[errorKey];
    let formatted = label;

    if (errorKey === 'minlength' && error?.['requiredLength']) {
      formatted = label.replace('{min}', error['requiredLength']);
    } else if (errorKey === 'maxlength' && error?.['requiredLength']) {
      formatted = label.replace('{max}', error['requiredLength']);
    }

    return formatted;
  }

  focusField(controlName: string): void {
    const fieldLabel = this.getFieldLabel(controlName);
    const labels = Array.from(document.querySelectorAll('label'));
    const matchingLabel = labels.find(label => label.textContent?.trim().startsWith(fieldLabel));

    if (matchingLabel?.htmlFor) {
      const input = document.getElementById(matchingLabel.htmlFor);
      if (input) {
        input.focus();
        input.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }
}
